import {AttrsSchema, Component} from 'panel';

import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {DateKeyedFunnelSegments, SelectMode, TrendChartType} from 'common/report/models/funnels/types';
import {ChartDisplayContext} from 'common/widgets/legend-line-chart';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {getLegendSections} from 'common/report/models/funnels/utils';
import {hasData} from 'common/report/util/funnels/utils';
import {orderSegmentsbyTotalConversions} from 'common/report/data/funnels/utils';
import {ODA_FUNNELS_UPSELL_DESCRIPTION} from 'common/widgets/oda-control/constants';

import {getXAxisLabelStepAndStagger} from './utils';
import {formatTooltip, formatXAxisLabel, formatYAxisLabel, getChartData} from './formatters';
import template from './index.jade';
import css from './index.styl?inline';

interface ChartAttrs {
  'display-context': ChartDisplayContext;
  'download-mode': boolean;
  'date-axis-override': number[];
  'event-index': null | number;
  'hide-analysis-button': boolean;
  'incomplete-from-date': boolean;
  'incomplete-to-date': boolean;
  'is-compact': boolean;
  'is-waiting-for-schedule-analysis': boolean;
  'legend-theme': ChartLegendTheme;
  selected: ColorChoice[];
  'show-on-demand-analysis-upsell': boolean;
  'select-mode': SelectMode;
  'show-annotations': boolean;
  'trend-type': TrendChartType;
}

export class FunnelsConversionOverTimeChart extends Component<EmptyObj, ChartAttrs> {
  private _segmentData: DateKeyedFunnelSegments[] = [];
  private _comparisonSegmentData: DateKeyedFunnelSegments[] = [];
  public funnelsQuery: FunnelsQuery;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      'date-axis-override': {
        type: `json`,
        description: `dates of the tick marks for highcharts`,
      },
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'download-mode': `boolean`,
      'event-index': {type: `number`, default: null},
      'hide-analysis-button': `boolean`,
      'incomplete-from-date': `boolean`,
      'incomplete-to-date': `boolean`,
      'is-compact': `boolean`,
      'is-waiting-for-schedule-analysis': `boolean`,
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      selected: {type: `json`, default: []},
      'select-mode': {type: `string`, enum: Object.values(SelectMode), default: SelectMode.Position},
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'show-on-demand-analysis-upsell': `boolean`,
      'trend-type': {type: `string`, enum: Object.values(TrendChartType), default: TrendChartType.Conversion},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {
        ODA_FUNNELS_UPSELL_DESCRIPTION,

        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },

        clickAnalyze: () => this.dispatchEvent(new CustomEvent(`clickAnalyze`)),

        hasNoData: () => !hasData(this.segmentData),

        getLegendSections: () =>
          getLegendSections(this.attr(`selected`), this.attr(`select-mode`), this.funnelsQuery, this.segmentData),

        getChartData: () =>
          getChartData({
            comparedTrends: this.comparisonSegmentData,
            eventIndex: this.attr(`event-index`),
            funnelsQuery: this.funnelsQuery,
            orderedTrends: this.segmentData,
            selected: this.attr(`selected`),
            selectMode: this.attr(`select-mode`),
            trendType: this.attr(`trend-type`),
          }),

        getXAxisOptions: () => {
          const {dateRange, trendUnit} = this.funnelsQuery;
          return this.attr(`date-axis-override`)
            ? {
                tickPositions: this.attr(`date-axis-override`),
                labels: getXAxisLabelStepAndStagger(dateRange, trendUnit),
              }
            : {};
        },

        getTrendUnit: () => this.funnelsQuery.trendUnit,

        formatYAxisLabel: (value: number) => formatYAxisLabel(value, this.attr(`trend-type`)),

        formatXAxisLabel: (value: number) => formatXAxisLabel(value, this.funnelsQuery.trendUnit),

        formatTooltip: (x: number, _y: number, series: any, _idx: number) =>
          formatTooltip({
            comparedTrends: this.comparisonSegmentData,
            eventIndex: this.attr(`event-index`),
            funnelsQuery: this.funnelsQuery,
            isCompact: this.attr(`is-compact`),
            orderedTrends: this.segmentData,
            trendType: this.attr(`trend-type`),
            x,
            series,
          }),
      },
    };
  }

  public set segmentData(val: DateKeyedFunnelSegments[]) {
    this._segmentData = orderSegmentsbyTotalConversions(val);
  }

  public get segmentData(): DateKeyedFunnelSegments[] {
    return this._segmentData;
  }

  public set comparisonSegmentData(val: DateKeyedFunnelSegments[]) {
    this._comparisonSegmentData = orderSegmentsbyTotalConversions(val);
  }

  public get comparisonSegmentData(): DateKeyedFunnelSegments[] {
    return this._comparisonSegmentData;
  }
}
customElements.define(`mp-funnels-conversion-over-time-chart`, FunnelsConversionOverTimeChart);
